/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/ShiftSwapModern.tsx
 * PURPOSE: Staff self-service shift swap request page — submit, view, cancel
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Shift Operations, Employee Self-Service
 * - Impact: Revenue Critical — Shift swap prevents under-staffing
 * - Stakeholders: Service Staff (NQH), Branch Managers, Operations Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Router, Page Layout, Component Composition
 * - Dependencies: React 18, React Router, TypeScript, apiWrapper
 *
 * @cross-references
 * - Design: docs/02-design/10-bpm-architecture/GENERIC-5-STATE-WORKFLOW-ENGINE-DESIGN.md
 * - API: docs/03-integrate/01-API-Specifications/ (POST /api/v1/shifts/swap)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ShiftSwapModern.module.css';
import { toLocalISODate } from '@/utils/date';

interface SwapRequest {
  id: string;
  status: string;
  swap_date?: string;
  reason: string;
  requester_name?: string;
  target_name?: string;
  created_at: string;
}

const STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Đã gửi',
  PENDING: 'Đang chờ',
  MANAGER_REVIEW: 'Chờ quản lý',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
  CANCELLED: 'Đã hủy',
};

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      const tenantKey = parsed?.state?.tenantKey || parsed?.state?.tenant_key;
      if (tenantKey) headers['X-Tenant-Key'] = tenantKey;
    }
  } catch { /* ignore */ }
  return headers;
}

function futureDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalISODate(d);
}

export const ShiftSwapModern: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<SwapRequest[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<SwapRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [swapDate, setSwapDate] = useState(futureDate(3));
  const [reason, setReason] = useState('');
  const [preferredPeer, setPreferredPeer] = useState('');

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const [ownRes, incomingRes] = await Promise.all([
        fetch('/api/v1/shifts/swap', { headers: getAuthHeaders() }),
        fetch('/api/v1/shifts/swap?as_target=true', { headers: getAuthHeaders() }),
      ]);
      if (ownRes.ok) {
        const data = await ownRes.json();
        setRequests(Array.isArray(data) ? data : (data.items || data.data || []));
      } else {
        setRequests([]);
      }
      if (incomingRes.ok) {
        const data = await incomingRes.json();
        setIncomingRequests(Array.isArray(data) ? data : (data.items || data.data || []));
      } else {
        setIncomingRequests([]);
      }
    } catch {
      setRequests([]);
      setIncomingRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const body: Record<string, string> = {
        swap_date: swapDate,
        reason: reason,
      };
      const peer = preferredPeer.trim();
      if (peer) {
        // Send as email if it looks like one, otherwise search by name (backend accepts both)
        body['preferred_peer_email'] = peer;
      }

      const res = await fetch('/api/v1/shifts/swap', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok || res.status === 201) {
        setMessage('Yêu cầu đổi ca đã được gửi thành công.');
        setShowForm(false);
        setReason('');
        setPreferredPeer('');
        setSwapDate(futureDate(3));
        // Prepend the new request to the list
        if (data && data.id) {
          setRequests(prev => [{
            id: String(data.id),
            status: data.status || 'REQUESTED',
            swap_date: data.swap_date || swapDate,
            reason: data.reason || reason,
            requester_name: data.requester_name,
            target_name: data.target_employee_name,
            created_at: data.created_at || new Date().toISOString(),
          }, ...prev]);
        } else {
          await loadRequests();
        }
      } else {
        const errMsg = data?.detail?.message_vi || data?.detail?.message || data?.message_vi || data?.message || data?.detail || 'Không thể gửi yêu cầu đổi ca.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (swapId: string) => {
    try {
      const res = await fetch(`/api/v1/shifts/swap/${swapId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setMessage('Đã từ chối yêu cầu đổi ca.');
        setIncomingRequests(prev =>
          prev.map(r => r.id === swapId ? { ...r, status: 'REJECTED' } : r)
        );
      } else {
        const data = await res.json().catch(() => ({}));
        const errMsg = data?.message_vi || data?.detail?.message_vi || data?.detail || 'Không thể từ chối yêu cầu.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    }
  };

  const handleAccept = async (swapId: string) => {
    try {
      const res = await fetch(`/api/v1/shifts/swap/${swapId}/accept`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setMessage('Đã chấp nhận yêu cầu đổi ca. Chờ quản lý duyệt.');
        setIncomingRequests(prev =>
          prev.map(r => r.id === swapId ? { ...r, status: 'MANAGER_REVIEW' } : r)
        );
      } else {
        const data = await res.json().catch(() => ({}));
        const errMsg = data?.message_vi || data?.detail?.message_vi || data?.detail || 'Không thể chấp nhận yêu cầu.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    }
  };

  const handleCancel = async (swapId: string) => {
    try {
      const res = await fetch(`/api/v1/shifts/swap/${swapId}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setMessage('Yêu cầu đã được hủy.');
        setRequests(prev =>
          prev.map(r => r.id === swapId ? { ...r, status: 'CANCELLED' } : r)
        );
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.message_vi || data?.detail || 'Không thể hủy yêu cầu.');
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 id="shift-swap-title" className={styles.title}>🔄 Đổi Ca</h1>
          <button onClick={() => navigate('/dashboard')} className={styles.backButton}>
            Về Dashboard
          </button>
        </div>
      </header>

      <main aria-labelledby="shift-swap-title" className={styles.main}>

        {/* Actions row */}
        <div className={styles.actionsRow}>
          <button
            onClick={() => { setShowForm(!showForm); setError(null); setMessage(null); }}
            className={styles.primaryButton}
            data-testid="btn-new-swap"
          >
            {showForm ? 'Đóng form' : 'Tạo yêu cầu'}
          </button>
        </div>

        {/* Status messages */}
        {message && (
          <p
            className={styles.statusSuccess}
            data-testid="swap-success-msg"
            role="alert"
            aria-live="polite"
          >
            {message}
          </p>
        )}
        {error && (
          <p className={styles.statusError} data-testid="swap-error-msg">{error}</p>
        )}

        {/* New swap request form */}
        {showForm && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Yêu cầu đổi ca mới</h2>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="swap-date" className={styles.label}>
                  Ngày cần đổi ca
                </label>
                <input
                  id="swap-date"
                  type="date"
                  name="swap_date"
                  value={swapDate}
                  onChange={e => setSwapDate(e.target.value)}
                  min={futureDate(1)}
                  required
                  className={styles.input}
                  data-testid="swap-date-input"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="swap-reason" className={styles.label}>
                  Lý do đổi ca
                </label>
                <textarea
                  id="swap-reason"
                  name="reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Nhập lý do đổi ca (ít nhất 5 ký tự)"
                  minLength={5}
                  maxLength={500}
                  required
                  className={styles.textarea}
                  data-testid="swap-reason-input"
                  rows={3}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="swap-peer" className={styles.label}>
                  Email đồng nghiệp (không bắt buộc)
                </label>
                <input
                  id="swap-peer"
                  type="text"
                  name="target_employee"
                  value={preferredPeer}
                  onChange={e => setPreferredPeer(e.target.value)}
                  placeholder="email@nqh.com.vn"
                  className={styles.input}
                  data-testid="swap-target-input"
                />
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  disabled={submitting}
                  className={styles.primaryButton}
                  data-testid="swap-submit-btn"
                >
                  {submitting ? 'Đang gửi...' : 'Gửi yêu cầu'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={styles.secondaryButton}
                >
                  Hủy
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Incoming swap requests (from colleagues targeting me) */}
        {incomingRequests.length > 0 && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Yêu cầu đến từ đồng nghiệp</h2>
            <ul className={styles.requestList}>
              {incomingRequests.map(req => (
                <li key={req.id} className={styles.requestItem} data-testid={`incoming-swap-${req.id}`}>
                  <div className={styles.requestHeader}>
                    <span
                      className={styles.statusBadge}
                      data-status={req.status}
                      data-testid="incoming-status-badge"
                    >
                      {STATUS_LABELS[req.status] || req.status}
                    </span>
                    <span className={styles.requestDate}>{req.swap_date || ''}</span>
                  </div>
                  <p className={styles.requestReason}>{req.reason}</p>
                  {req.requester_name && (
                    <p className={styles.requestPeer}>Từ: {req.requester_name}</p>
                  )}
                  <div className={styles.requestFooter}>
                    <span className={styles.requestTime}>
                      {new Date(req.created_at).toLocaleDateString('vi-VN')}
                    </span>
                    {req.status === 'REQUESTED' && (
                      <>
                        <button
                          onClick={() => handleAccept(req.id)}
                          className={styles.primaryButton}
                          data-testid={`btn-accept-swap`}
                          aria-label={`Chấp nhận yêu cầu đổi ca`}
                        >
                          Chấp nhận
                        </button>
                        <button
                          onClick={() => handleReject(req.id)}
                          className={styles.cancelButton}
                          data-testid={`btn-reject-swap`}
                          aria-label={`Từ chối yêu cầu đổi ca`}
                        >
                          Từ chối
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Swap requests list */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Danh sách yêu cầu đổi ca</h2>
          {loading ? (
            <p className={styles.cardDescription}>Đang tải...</p>
          ) : requests.length === 0 ? (
            <p className={styles.cardDescription}>
              Chưa có yêu cầu đổi ca nào. Nhấn "Tạo yêu cầu" để gửi yêu cầu mới.
            </p>
          ) : (
            <ul className={styles.requestList}>
              {requests.map(req => (
                <li key={req.id} className={styles.requestItem} data-testid={`swap-request-${req.id}`}>
                  <div className={styles.requestHeader}>
                    <span
                      className={styles.statusBadge}
                      data-status={req.status}
                      data-testid="swap-status-badge"
                    >
                      {STATUS_LABELS[req.status] || req.status}
                    </span>
                    <span className={styles.requestDate}>
                      {req.swap_date || ''}
                    </span>
                  </div>
                  <p className={styles.requestReason}>{req.reason}</p>
                  {req.target_name && (
                    <p className={styles.requestPeer}>Đồng nghiệp: {req.target_name}</p>
                  )}
                  <div className={styles.requestFooter}>
                    <span className={styles.requestTime}>
                      {new Date(req.created_at).toLocaleDateString('vi-VN')}
                    </span>
                    {(req.status === 'REQUESTED' || req.status === 'PENDING') && (
                      <button
                        onClick={() => handleCancel(req.id)}
                        className={styles.cancelButton}
                        data-testid={`cancel-swap-${req.id}`}
                      >
                        Hủy yêu cầu
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

      </main>
    </div>
  );
};

export default ShiftSwapModern;
